"use client";

import { Switch, Tabs, TabsList, TabsTrigger } from "@upstageai/ui";

import { ConfigModelSelect } from "../ConfigModelSelect";
import { ConfigOption } from "../ConfigOption";
import { ConfigSidebarDisabledInfoTooltip } from "./ConfigSidebarComponents";
import { ConfigSidebarLayout } from "./ConfigSidebarLayout";
import { useConfigSidebar } from "./useConfigSidebar";
import { CheckboxWithLabel } from "../../../components/Checkbox/CheckboxWithLabel";
import {
  BASE64_ENCODING_OPTIONS,
  DEFAULT_DP_INFERENCE_CONFIG,
  DEFAULT_DP_MODE,
  OUTPUT_FORMATS_OPTIONS,
} from "../../../constants/DpInferenceConfig";
import { EndpointType } from "../../../constants/Endpoints";
import {
  DpInferenceConfig,
  DpInferenceConfigField,
  OcrModeType,
  DpModelType,
  ModeType,
} from "../../../types/dpInferenceConfig";
import { configStore } from "../../../utils/mobx/ConfigStore";

export default function DocumentParseSidebar({
  isOpen,
  onClose,
  className,
  endpointType,
}: {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  endpointType: EndpointType;
}) {
  const {
    config,
    setConfig,
    isOverflowing,
    contentRef,
    isMobile,
    inferenceState,
    accessibleModels,
    handleReset,
    handleApply,
  } = useConfigSidebar<DpInferenceConfig>({
    endpointType,
    onClose,
    getCurrentConfig: () => configStore.currentDPConfig,
    defaultConfig: DEFAULT_DP_INFERENCE_CONFIG,
    resetStoreConfig: () => configStore.resetDPConfig(),
    getResetConfig: () => configStore.currentDPConfig,
  });

  // 설정 변경
  const handleChange = (
    field: DpInferenceConfigField,
    value: ModeType | OcrModeType | boolean | object | DpModelType,
  ) => {
    // 모델 변경 시: mode를 유지하면서 model만 변경
    if (field === "model") {
      const newModel = value as DpModelType;
      const nextConfig: DpInferenceConfig = {
        ...config,
        mode: config.mode || DEFAULT_DP_MODE,
        model: newModel,
      };

      setConfig(nextConfig);
      configStore.setDPConfig(nextConfig);
      return;
    }

    // 그 외 필드는 기존 config를 기준으로 부분 업데이트
    const newConfig: DpInferenceConfig = {
      ...(config as DpInferenceConfig),
      [field]: value,
    } as DpInferenceConfig;

    setConfig(newConfig);
    configStore.setDPConfig(newConfig);
  };

  const showChartRecognition = (config: DpInferenceConfig) => {
    return config.mode === "standard";
  };

  return (
    <ConfigSidebarLayout
      isOpen={isOpen}
      onClose={onClose}
      className={className}
      isMobile={isMobile}
      isOverflowing={isOverflowing}
      contentRef={contentRef}
      onReset={handleReset}
      onApply={handleApply}
      disabled={inferenceState.isInfering}
    >
      <ConfigOption label="Model" orientation="vertical">
        <ConfigModelSelect
          value={config.model}
          onValueChange={value => handleChange("model", value as DpModelType)}
          disabled={inferenceState.isInfering}
          accessibleModels={accessibleModels}
        />
      </ConfigOption>

      <ConfigOption
        label="Mode"
        description={
          <>
            Standard : For clear, structured documents
            <br />
            Enhanced : For difficult layouts & poor scans
          </>
        }
        orientation="vertical"
      >
        <Tabs
          value={config.mode}
          onValueChange={value => handleChange("mode", value as ModeType)}
          className="h-9 w-fit"
        >
          <TabsList className="grid grid-cols-2">
            <TabsTrigger
              value={"standard" as ModeType}
              disabled={inferenceState.isInfering}
            >
              Standard
            </TabsTrigger>
            <TabsTrigger
              value={"enhanced" as ModeType}
              disabled={inferenceState.isInfering}
            >
              Enhanced
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </ConfigOption>

      <ConfigOption
        label="OCR Mode"
        description={
          <>
            Auto : OCR on images only <br />
            Force : Always apply OCR
          </>
        }
        orientation="vertical"
      >
        <Tabs
          value={config.ocr}
          onValueChange={value => handleChange("ocr", value as OcrModeType)}
          className="h-9 w-fit"
        >
          <TabsList className="grid grid-cols-2">
            <TabsTrigger
              value={"auto" as OcrModeType}
              disabled={inferenceState.isInfering}
            >
              Auto
            </TabsTrigger>
            <TabsTrigger
              value={"force" as OcrModeType}
              disabled={inferenceState.isInfering}
            >
              Force
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </ConfigOption>

      <ConfigOption
        label="Merge Multipage Tables"
        description="Merge tables that are split across multiple pages into a single table."
      >
        <Switch
          checked={!!config.mergeMultipageTables}
          onCheckedChange={value => handleChange("mergeMultipageTables", value)}
          disabled={inferenceState.isInfering}
        />
      </ConfigOption>

      {showChartRecognition(config) && (
        <ConfigOption
          label="Chart Recognition"
          description="Use chart recognition"
        >
          <Switch
            checked={!!config.chartRecognition}
            onCheckedChange={value => handleChange("chartRecognition", value)}
            disabled={inferenceState.isInfering}
          />
        </ConfigOption>
      )}

      <ConfigOption label="Coordinates" description="Return bounding boxes">
        <ConfigSidebarDisabledInfoTooltip className="mr-2">
          <Switch
            checked={!!config.coordinates}
            onCheckedChange={value => handleChange("coordinates", value)}
            disabled={true}
          />
        </ConfigSidebarDisabledInfoTooltip>
      </ConfigOption>

      <ConfigOption
        label="Output Formats"
        description="Select output format(s) for layout elements"
        orientation="vertical"
      >
        <div className="mb-4 flex w-full justify-between">
          {OUTPUT_FORMATS_OPTIONS.map(({ key, label }) => (
            <ConfigSidebarDisabledInfoTooltip
              className="ml-[152px]"
              key={key}
              showTooltip={DEFAULT_DP_INFERENCE_CONFIG.outputFormats.includes(
                key,
              )}
            >
              <CheckboxWithLabel
                id={`output-format-${key}`}
                label={label}
                isChecked={config.outputFormats?.includes(key)}
                onChange={checked => {
                  if (DEFAULT_DP_INFERENCE_CONFIG.outputFormats.includes(key)) {
                    return;
                  }

                  const currentOutputFormats = config.outputFormats || [];
                  const newOutputFormats = checked
                    ? [...currentOutputFormats, key]
                    : currentOutputFormats.filter(item => item !== key);

                  handleChange("outputFormats", newOutputFormats);
                }}
                disabled={
                  inferenceState.isInfering ||
                  DEFAULT_DP_INFERENCE_CONFIG.outputFormats.includes(key)
                }
              />
            </ConfigSidebarDisabledInfoTooltip>
          ))}
        </div>
      </ConfigOption>

      <ConfigOption
        label="Base64 Encoding"
        description="Set whether to extract layout elements as base64"
        orientation="vertical"
      >
        <div className="mb-4 grid w-full grid-cols-2 gap-y-3">
          {BASE64_ENCODING_OPTIONS.map(({ key, label }) => (
            <ConfigSidebarDisabledInfoTooltip
              key={key}
              className="ml-[94px]"
              showTooltip={DEFAULT_DP_INFERENCE_CONFIG.base64Encoding.includes(
                key,
              )}
            >
              <CheckboxWithLabel
                id={`base64-${key}`}
                label={label}
                isChecked={config.base64Encoding?.includes(key)}
                onChange={checked => {
                  if (
                    DEFAULT_DP_INFERENCE_CONFIG.base64Encoding.includes(key)
                  ) {
                    return;
                  }

                  const currentBase64Encoding = config.base64Encoding || [];
                  const newBase64Encoding = checked
                    ? [...currentBase64Encoding, key]
                    : currentBase64Encoding.filter(item => item !== key);

                  handleChange("base64Encoding", newBase64Encoding);
                }}
                disabled={
                  inferenceState.isInfering ||
                  DEFAULT_DP_INFERENCE_CONFIG.base64Encoding.includes(key)
                }
              />
            </ConfigSidebarDisabledInfoTooltip>
          ))}
        </div>
      </ConfigOption>
    </ConfigSidebarLayout>
  );
}
